/**
 * Profile and sign-in (Figma 09 · Form pages 01–05, client C03/C04).
 * Not built, no backend yet: edit name, change photo, a Google account
 * setting a first password, the device list, closing the account.
 */
import { useState } from "react";
import { useForm } from "react-hook-form";
import { LogOut } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useChangePassword } from "@/app/routes/dashboard/settings/api/usePassword";
import { KitModal } from "../../kit/Modal";
import { KitButton } from "../../kit/Button";
import { KitPasswordInput } from "../../kit/PasswordInput";
import {
  Card,
  DetailHead,
  Fact,
  Facts,
  PageHead,
  PersonMark,
  QuietNote,
  SectionHead,
  SettingsPage,
  TextAction,
} from "../parts/SettingsParts";
import { useYouHead } from "../parts/scope";

// Same floor as the backend's v_ChangePassword.
// The rule for every new password (Figma 10 · D5; backend v_NewPassword).
const MIN_PASSWORD_LENGTH = 10;

type PasswordFields = { currentPassword: string; newPassword: string; confirmPassword: string };

/**
 * Change your password (Form pages / 03). Not optimistic: the form keeps its
 * values when the current password is wrong. Saving signs the other devices out.
 */
export function ChangePasswordForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const changePassword = useChangePassword();
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<PasswordFields>();

  return (
    <form
      noValidate
      className="flex w-full flex-col gap-4"
      onSubmit={handleSubmit(({ currentPassword, newPassword }) =>
        changePassword.mutate({ currentPassword, newPassword }, { onSuccess: onDone }),
      )}
    >
      <KitPasswordInput
        label="Current password"
        autoComplete="current-password"
        autoFocus
        error={errors.currentPassword}
        {...register("currentPassword", { required: "Type your current password" })}
      />
      <KitPasswordInput
        label="New password"
        autoComplete="new-password"
        error={errors.newPassword}
        {...register("newPassword", {
          required: "Type a new password",
          minLength: { value: MIN_PASSWORD_LENGTH, message: `Use at least ${MIN_PASSWORD_LENGTH} characters` },
          validate: (v) => v !== getValues("currentPassword") || "Use a password different from the current one",
        })}
      />
      <KitPasswordInput
        label="New password again"
        autoComplete="new-password"
        error={errors.confirmPassword}
        {...register("confirmPassword", {
          required: "Type the new password again",
          validate: (v) => v === getValues("newPassword") || "The two new passwords are different",
        })}
      />
      <QuietNote>Forgot the current one? Sign out and use Forgot password on the sign-in page.</QuietNote>
      <div className="flex items-center justify-end gap-3 pt-2">
        <KitButton type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </KitButton>
        <KitButton type="submit" loading={changePassword.isPending}>
          Change password
        </KitButton>
      </div>
    </form>
  );
}

export default function ProfilePage() {
  const { user, logout, selectedOrganization } = useCurrentUser();
  const head = useYouHead();
  const [changing, setChanging] = useState(false);
  const isGuest = selectedOrganization?.role === "Guest";
  const hasPassword = !!user?.hasPassword;
  const name = user?.name || user?.email || "";

  return (
    <SettingsPage>
      <PageHead
        {...head}
        title="Profile and sign-in"
        status={isGuest ? "Your name, and how you sign in to review your brands." : "Your name and how you sign in."}
      />
      <Card>
        <DetailHead lead={<PersonMark name={name} picture={user?.profilePicture} size={56} />} title={name} sub={user?.email} />
        <Facts>
          <Fact label="Name">{user?.name || "Not set"}</Fact>
          <Fact label="Email">
            {user?.email} <span className="font-normal text-co-muted">· ask Mohtawa to change it</span>
          </Fact>
        </Facts>

        <SectionHead title="Sign-in" meta={hasPassword ? "password" : "Google"} />
        <Facts>
          {hasPassword ? (
            <Fact label="Password">Set</Fact>
          ) : (
            <>
              <Fact label="Password">Not set. You sign in with Google.</Fact>
              <Fact label="Google">{user?.email}</Fact>
            </>
          )}
        </Facts>
        {hasPassword ? (
          <KitButton variant="secondary" className="self-start" onClick={() => setChanging(true)}>
            Change password
          </KitButton>
        ) : null}

        {isGuest ? (
          <>
            <SectionHead title="Leaving" />
            <QuietNote>To close your login, ask the team that invited you. Your comments stay with the brand.</QuietNote>
            <TextAction icon={LogOut} onPress={logout}>
              Sign out
            </TextAction>
          </>
        ) : null}
      </Card>

      {changing ? (
        <KitModal
          isOpen
          onOpenChange={(open) => (open ? undefined : setChanging(false))}
          title="Change your password"
          description="Saving signs you out on your other devices; this one stays signed in."
          modalClassName="!w-[min(calc(100vw-2rem),480px)] !max-w-[480px]"
        >
          <ChangePasswordForm onDone={() => setChanging(false)} onCancel={() => setChanging(false)} />
        </KitModal>
      ) : null}
    </SettingsPage>
  );
}

